import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Button variants for different use cases
 */
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

/**
 * Button sizes
 */
type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Props for the Button component
 */
export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** The visual style variant of the button */
  variant?: ButtonVariant;
  /** The size of the button */
  size?: ButtonSize;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /** Icon to display before the button text */
  leftIcon?: ReactNode;
  /** Icon to display after the button text */
  rightIcon?: ReactNode;
  /** The button content */
  children: ReactNode;
  /** Full width button */
  fullWidth?: boolean;
}

/**
 * Utility function to merge Tailwind classes
 */
const cn = (...inputs: (string | undefined | null | false)[]) => twMerge(clsx(inputs));

/**
 * Base styles for the glass effect
 */
const glassBase = `
  backdrop-blur-xl
  border
  transition-all
  duration-300
  ease-out
  font-medium
  inline-flex
  items-center
  justify-center
  gap-2
  focus:outline-none
  focus-visible:ring-2
  focus-visible:ring-offset-2
  focus-visible:ring-offset-slate-900
  disabled:opacity-50
  disabled:cursor-not-allowed
  disabled:pointer-events-none
`;

/**
 * Variant-specific styles
 */
const variantStyles: Record<ButtonVariant, string> = {
  // Schriftfarbe je Flaeche gerechnet, nicht geraten: Light Blue traegt in
  // *beiden* Themes dunkle Schrift (`on-secondary`, 6,7:1 bzw. 10,9:1) —
  // `text-surface-900` kippt im Dunklen auf Hell und landete bei 2,4:1.
  // Die Fehlerflaeche steht voll statt bei 80 %: durchscheinend erreicht sie
  // in keinem Theme 4,5:1 (dunkel 3,99, hell 3,80/4,19), voll dagegen 5,7
  // bzw. 6,2 mit `on-primary`.
  //
  // Feste Klassennamen, keine zusammengesetzten: Tailwind liest den Quelltext
  // und erzeugt nur, was woertlich darin steht. `bg-[${tokens.primary}]/80`
  // stand zwar im Markup, existierte im CSS aber nie — die Varianten waren
  // wirkungslos. Im gebauten Bundle war die Klasse nicht zu finden.
  primary: `
    bg-primary/80
    hover:bg-primary
    border-primary/40
    hover:border-primary/60
    text-on-primary
    focus-visible:ring-primary
    shadow-lg
    shadow-primary/25
    hover:shadow-primary/40
  `,
  secondary: `
    bg-secondary/80
    hover:bg-secondary
    border-secondary/40
    hover:border-secondary/60
    text-on-secondary
    focus-visible:ring-secondary
    shadow-lg
    shadow-secondary/25
    hover:shadow-secondary/40
  `,
  ghost: `
    bg-surface-200/60
    hover:bg-surface-200
    border-surface-300/60
    hover:border-surface-300
    text-surface-900
    focus-visible:ring-surface-400
  `,
  danger: `
    bg-error
    hover:bg-error/90
    border-error/40
    hover:border-error/60
    text-on-primary
    focus-visible:ring-error
    shadow-lg
    shadow-error/25
    hover:shadow-error/40
  `,
};

/**
 * Size-specific styles
 */
const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm rounded-lg',
  md: 'px-5 py-2.5 text-base rounded-xl',
  lg: 'px-8 py-3.5 text-lg rounded-2xl',
};

/**
 * Spinner component for loading state
 */
const Spinner = ({ size }: { size: ButtonSize }) => {
  const spinnerSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  return (
    <motion.svg
      className={cn(spinnerSizes[size], 'animate-spin')}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </motion.svg>
  );
};

/**
 * Button component with Liquid Glass design
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md">
 *   Click me
 * </Button>
 *
 * <Button variant="secondary" loading>
 *   Processing...
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      fullWidth,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const motionProps: HTMLMotionProps<'button'> = {
      whileHover: isDisabled ? undefined : { scale: 1.02 },
      whileTap: isDisabled ? undefined : { scale: 0.98 },
      transition: { type: 'spring', stiffness: 400, damping: 17 },
    };

    return (
      <motion.button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          glassBase,
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        aria-busy={loading}
        aria-disabled={isDisabled}
        {...motionProps}
        {...(props as Omit<HTMLMotionProps<'button'>, 'ref'>)}
      >
        {loading ? (
          <Spinner size={size} />
        ) : (
          leftIcon && <span className="flex-shrink-0">{leftIcon}</span>
        )}
        {/* Flex-Zeile statt Inline-Element: `svg` ist in Tailwind `display: block`
            und bricht als Kind in einem Inline-Element die Zeile um (Symbol links
            über dem Text). Kein `gap` — die Aufrufer setzen `mr-1`/`ml-2` selbst. */}
        <span className={cn('inline-flex items-center', loading && 'opacity-70')}>{children}</span>
        {rightIcon && !loading && <span className="flex-shrink-0">{rightIcon}</span>}
      </motion.button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
